/** @jsxImportSource @emotion/react */

import { CalciteLoader } from "@esri/calcite-components-react";
import { Button } from "components/Button";
import { Card } from "components/Card";
import { Input } from "components/Input";
import { fetchDiscrepancyCount } from "components/Layout/SideNav";
import Logo from "components/Logo/Logo";
import { UserContext } from "context/UserContext";
import { useContext, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router";
import { AuthService } from "services/Auth";
import UserService from "services/User";
import tw from "twin.macro";

function Home() {
  const navigate = useNavigate();
  const authService = new AuthService();
  const userService = new UserService();

  const {
    user,
    resetUser,
    isLoggedIn,
    setUserOtp,
    signout,
    setDiscrepancyCount,
  } = useContext(UserContext);
  const [redirectUrl, setRedirectUrl] = useState<string>("");

  const [otp, setOtp] = useState("");
  const [userOTPRequired, setUserOtpRequired] = useState(false);
  const [otpError, setOtpError] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);

  async function getFirstAvailableRoute(userPermissions) {
    if (userPermissions.includes("foresite:discrep:updateByJurisdiction")) {
      return "/discrepancies";
    }

    if (userPermissions.includes("foresite:discrep:updateByCarrier")) {
      // wait until user is not null, there are some race conditions
      let currentUser = user;
      if (!currentUser) {
        while (!currentUser) {
          await new Promise((resolve) => setTimeout(resolve, 100));
          currentUser = user;
        }
      }

      const discrepCount = await fetchDiscrepancyCount(currentUser);
      setDiscrepancyCount(discrepCount);

      if (discrepCount > 0) {
        return "/discrepancies";
      }
    }

    if (userPermissions.includes("foresite:dashboard")) return "/dashboard";

    if (
      userPermissions.includes("foresite:ali:view") ||
      userPermissions.includes("foresite:ali:write")
    )
      return "/ali-records";

    if (userPermissions.includes("foresite:ali_error:view"))
      return "/ali-error-records";

    if (
      userPermissions.includes("foresite:msag:view") ||
      userPermissions.includes("foresite:msag:requestChange")
    )
      return "/msag-records";
  }

  useEffect(() => {
    async function handleAuth() {
      // if the page has url params retrieve them
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get("code");
      // if there is a code param, the user is coming from the auth server
      if (code) {
        try {
          // sign in with keycloak
          await authService.login(code);

          // validate the session  with keycloak
          await authService.validate_session();

          // wait until the user is set in localStorage by the AuthService
          let currentUser = await waitUntilUserSet();

          // if the user is required to use OTP, show the OTP form
          if (
            currentUser?.otpRequired === true &&
            currentUser?.invalidOtp !== true
          ) {
            console.log("OTP required, showing OTP form.");
            setUserOtpRequired(true);
            //remove the url params to prevent retriggering
            const newUrl =
              window.location.protocol +
              "//" +
              window.location.host +
              window.location.pathname;
            window.history.replaceState({ path: newUrl }, "", newUrl);
            return;
          }

          // if the user entered an invalid OTP, show an error
          if (currentUser?.invalidOtp === true) {
            setOtpError(true);
            setOtpLoading(false);
            return;
          }

          // if the user is valid, redirect to the first available page
          if (currentUser) {
            const route = await getFirstAvailableRoute(currentUser?.functions);
            if (route) setRedirectUrl(route);
            else {
              console.log("No available route found for user permissions.");
              signout();
            }
          } else {
            console.log(
              "No Foresite user found after trying for 3000ms, logging out."
            );
            signout();
          }

          // clear any saved filters from localStorage
          // localStorage.removeItem("aliRecords-criteria");
          // localStorage.removeItem("msagRecords-criteria");
          // localStorage.removeItem("aliErrorRecords-criteria");
          // localStorage.removeItem("discrepancyRecords-criteria");
          // localStorage.removeItem("ELTRecords-criteria");

          // if the login was successful, redirect to the home page
          navigate(redirectUrl);
        } catch (err) {
          console.log("Error during authentication:", err);
          signout();
        }
        return;
      }

      // if there is no code param and the user is required to use OTP, show the OTP form
      let currentUser = await waitUntilUserSet();
      console.log("Current User:", currentUser);

      // Check if OTP is required
      if (currentUser?.otpRequired) {
        console.log("OTP required, showing OTP form.");
        setUserOtpRequired(true);
        return;
      }
      // if the user entered an invalid OTP, show an error
      else if (currentUser?.invalidOtp) {
        console.log("Invalid OTP, signing out.");
        // wait a second to show the error message
        setOtpError(true);
        setOtpLoading(false);
        await new Promise((resolve) => setTimeout(resolve, 3000));
        signout();
        return;
      }
      // if there is no code param and the user is already set, redirect to the first available page
      else {
        if (
          currentUser &&
          !currentUser?.otpRequired &&
          !currentUser?.invalidOtp
        ) {
          const route = await getFirstAvailableRoute(currentUser?.functions);
          if (route) setRedirectUrl(route);
        } else {
          console.log(
            "No Foresite user found after trying for 3000ms, logging out."
          );
          signout();
        }
      }
    }

    async function initializeUserRoute() {
      if (user && !user.otpRequired && !user.invalidOtp) {
        setUserOtpRequired(false);
        console.log("User logged in, redirecting.");
        const route = await getFirstAvailableRoute(user?.functions);
        if (route) setRedirectUrl(route);
      } else {
        console.log("No Foresite user found, following login flow.");
        handleAuth();
      }
    }

    initializeUserRoute();
  }, [user, user?.otpRequired, user?.invalidOtp, otpLoading]);

  async function waitUntilUserSet() {
    let currentUser = userService.getUser();
    const start = Date.now();
    while (!currentUser && Date.now() - start < 3000) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      currentUser = userService.getUser();
      if (currentUser) {
        return currentUser;
      }
    }
    return currentUser;
  }

  // forms not working so adding enter key listener
  useEffect(() => {
    async function handleKeyPress(event: KeyboardEvent) {
      if (
        event.key === "Enter" &&
        userOTPRequired &&
        otp.length === 6 &&
        !otpLoading &&
        !otpError
      ) {
        setUserOtp(otp);
        resetUser();
        setOtpLoading(true);
      }
    }

    window.addEventListener("keydown", handleKeyPress);

    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [userOTPRequired, otp, otpLoading, otpError, setUserOtp, resetUser]);

  return user && !user?.otpRequired && !userOTPRequired && isLoggedIn ? (
    <Navigate to={redirectUrl} replace={true} />
  ) : user?.otpRequired || userOTPRequired ? (
    <div
      css={[tw`flex flex-col items-center justify-center h-screen relative`]}
    >
      <Card
        css={[
          tw`drop-shadow-lg rounded-2xl border border-gray-200 p-8 max-w-lg w-full h-[340px]`,
        ]}
      >
        <p
          style={{
            paddingBottom: "16px",
            fontSize: "12px",
            fontWeight: "500",
            cursor: "pointer",
            zIndex: 10,
            textDecoration: "none",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.textDecoration = "underline")
          }
          onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
          onClick={signout}
        >
          &lt; Restart Login
        </p>
        <div
          css={[
            tw`h-3 w-full px-4 py-2 mb-10 flex justify-center items-center`,
          ]}
        >
          <Logo withText={true} />
        </div>
        <div
          css={[
            tw` bottom-0 left-0 w-full p-8 flex flex-col gap-y-1 items-center justify-center`,
          ]}
        >
          <Input
            name={"otp"}
            label="One Time Code"
            value={otp}
            scale="l"
            labelSize="l"
            onChange={(e) => setOtp(e.target.value)}
            css={tw`w-full`}
            max={6}
            focused={true}
          />
          <Button
            onClick={async () => {
              setUserOtp(otp);
              resetUser();
              setOtpLoading(true);
            }}
            disabled={otp.length !== 6 || otpLoading || otpError}
            kind="brand"
            scale="l"
            css={tw`w-1/4`}
            isLoading={otpLoading}
          >
            Sign In
          </Button>
          <div css={tw`h-4 w-full flex items-center justify-center`}>
            {otpError && (
              <p css={[tw`text-xs text-red-600`]}>
                Invalid One Time Code, signing out.
              </p>
            )}
          </div>
        </div>
      </Card>
    </div>
  ) : (
    <div
      css={[tw`flex flex-col items-center justify-center h-screen relative`]}
    >
      <CalciteLoader label={"Loading"} />
    </div>
  );
}

export default Home;
